import { useEffect, useMemo, useState } from 'react'
import { pickComparisonCustomer } from '@/lib/invoiceCustomerDiff'
import { Link } from '@tanstack/react-router'
import { Eye, EyeOff, Lock } from 'lucide-react'
import type PocketBase from 'pocketbase'
import { useLiveQuery } from '@tanstack/react-db'

import { ContactSection, CustomerSection, InvoiceSection, SectionActions } from './CalloutDetailsSections'
import { ServiceDetailsShell } from './ServiceDetailsShell'
import { useServiceAmend } from './useServiceAmend'
import { isServiceLocked } from '@/lib/serviceLock'
import { JobAssignmentDialog } from '@/components/JobAssignmentDialog'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { FormCapsLabel } from '@/components/ui/form-caps-label'
import { FormFieldLabel } from '@/components/ui/form-field-label'
import { UnderlineInput } from '@/components/ui/underline-input'
import { UnderlineTextarea } from '@/components/ui/underline-textarea'
import { useDb } from '@/lib/dbContext'
import { getCurrentUnsuccessfulReason } from '@/lib/jobAssignments'
import { isEncryptedDevicePassword } from '@/lib/devicePassword'
import {
  findJobNumberTagConflict,
  isPocketBaseJobNumberTagUniqueError,
  normalizeJobNumberTag,
  type JobNumberTagConflict,
} from '@/lib/jobNumberTag'
import type { SearchOption } from '@/components/ui/Search'
import type { JobDetailsScreenProps } from '@/screens/useJobDetails'

function formatLocalDateTime(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

function formatAssignmentWindow(assignedAt: string, unassignedAt?: string) {
  const start = formatLocalDateTime(assignedAt)
  if (!unassignedAt) return `${start} to now`
  return `${start} to ${formatLocalDateTime(unassignedAt)}`
}

function formatBillingOutcome(
  billingOutcome?: 'OmniInvoice' | 'NoOmniInvoice' | 'PosPayment',
  invoiceId?: string,
) {
  if (billingOutcome === 'PosPayment') return 'POS payment'
  if (billingOutcome === 'NoOmniInvoice') return 'No Omni invoice'
  if (billingOutcome === 'OmniInvoice') {
    return invoiceId?.trim() ? `Omni invoice: ${invoiceId}` : 'Omni invoice'
  }
  if (invoiceId?.trim()) return `Omni invoice: ${invoiceId}`
  return '—'
}

export function JobDetailsScreen(props: JobDetailsScreenProps) {
  const {
  showHeader = true,
  serviceId,
  publicId,
  customerName,
  customerId,
  customerPublicId,
  linkedCustomer,
  contactAddress,
  contactName,
  contactPhone,
  contactEmail,
  invoiceName,
  invoiceAddress,
  invoiceEmail,
  invoicePhone,
  invoiceVat,
  deviceType,
  deviceDescription,
  devicePassword,
  numberTag,
  fault,
  peripherals,
  status,
  statusChanges = [],
  checkedInSignedAt,
  checkedInByName,
  checkedInNoSignatureReason,
  checkedInSignatureUrl,
  checkedOutSignatureUrl,
  checkedOutSignedAt,
  checkedOutByName,
  checkedOutName,
  checkedOutPhone,
  checkedOutNoSignatureReason,
  billingOutcome,
  invoiceId,
  assignedUserId,
  assignedUserName,
  assignmentHistory = [],
  getAuthorDisplayName,
  onSaveDetails,
  onChangeStatus,
  onChangeCustomer,
  onAssignJob,
  amending = false,
  } = props
  const db = useDb()
  const usersResult = useLiveQuery((q) => q.from({ users: db.usersCollection }))
  const [deviceTypeInput, setDeviceTypeInput] = useState(deviceType ?? '')
  const [deviceDescriptionInput, setDeviceDescriptionInput] = useState(deviceDescription ?? '')
  const [devicePasswordInput, setDevicePasswordInput] = useState(devicePassword ?? '')
  const [showDevicePassword, setShowDevicePassword] = useState(false)
  const [revealedDevicePassword, setRevealedDevicePassword] = useState<string | null>(null)
  const [revealDialogOpen, setRevealDialogOpen] = useState(false)
  const [revealPasscode, setRevealPasscode] = useState('')
  const [revealError, setRevealError] = useState<string | null>(null)
  const [isRevealing, setIsRevealing] = useState(false)
  const [numberTagInput, setNumberTagInput] = useState(numberTag ?? '')
  const [faultInput, setFaultInput] = useState(fault ?? '')
  const [peripheralsInput, setPeripheralsInput] = useState(peripherals ?? '')
  const [contactAddressInput, setContactAddressInput] = useState(contactAddress ?? '')
  const [contactNameInput, setContactNameInput] = useState(contactName ?? '')
  const [contactPhoneInput, setContactPhoneInput] = useState(contactPhone ?? '')
  const [contactEmailInput, setContactEmailInput] = useState(contactEmail ?? '')
  const [invoiceNameInput, setInvoiceNameInput] = useState(invoiceName ?? '')
  const [invoiceAddressInput, setInvoiceAddressInput] = useState(invoiceAddress ?? '')
  const [invoiceEmailInput, setInvoiceEmailInput] = useState(invoiceEmail ?? '')
  const [invoicePhoneInput, setInvoicePhoneInput] = useState(invoicePhone ?? '')
  const [invoiceVatInput, setInvoiceVatInput] = useState(invoiceVat ?? '')
  const [invoiceIdInput, setInvoiceIdInput] = useState(invoiceId ?? '')
  const [selectedCustomer, setSelectedCustomer] = useState<SearchOption | null>(
    customerId ? { id: customerId, name: customerName } : null,
  )
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false)
  const [tagConflict, setTagConflict] = useState<JobNumberTagConflict | null>(null)
  const [baseline, setBaseline] = useState({
    deviceType: deviceType ?? '',
    deviceDescription: deviceDescription ?? '',
    devicePassword: devicePassword ?? '',
    numberTag: numberTag ?? '',
    fault: fault ?? '',
    peripherals: peripherals ?? '',
    contactAddress: contactAddress ?? '',
    contactName: contactName ?? '',
    contactPhone: contactPhone ?? '',
    contactEmail: contactEmail ?? '',
    invoiceName: invoiceName ?? '',
    invoiceAddress: invoiceAddress ?? '',
    invoiceEmail: invoiceEmail ?? '',
    invoicePhone: invoicePhone ?? '',
    invoiceVat: invoiceVat ?? '',
    invoiceId: invoiceId ?? '',
    billingOutcome,
    customerId: customerId ?? null,
    customerName,
    status,
  })

  useEffect(() => {
    setDeviceTypeInput(deviceType ?? '')
    setDeviceDescriptionInput(deviceDescription ?? '')
    setDevicePasswordInput(devicePassword ?? '')
    setRevealedDevicePassword(null)
    setNumberTagInput(numberTag ?? '')
    setFaultInput(fault ?? '')
    setPeripheralsInput(peripherals ?? '')
    setContactAddressInput(contactAddress ?? '')
    setContactNameInput(contactName ?? '')
    setContactPhoneInput(contactPhone ?? '')
    setContactEmailInput(contactEmail ?? '')
    setInvoiceNameInput(invoiceName ?? '')
    setInvoiceAddressInput(invoiceAddress ?? '')
    setInvoiceEmailInput(invoiceEmail ?? '')
    setInvoicePhoneInput(invoicePhone ?? '')
    setInvoiceVatInput(invoiceVat ?? '')
    setInvoiceIdInput(invoiceId ?? '')
    setSelectedCustomer(customerId ? { id: customerId, name: customerName } : null)
    setBaseline({
      deviceType: deviceType ?? '',
      deviceDescription: deviceDescription ?? '',
      devicePassword: devicePassword ?? '',
      numberTag: numberTag ?? '',
      fault: fault ?? '',
      peripherals: peripherals ?? '',
      contactAddress: contactAddress ?? '',
      contactName: contactName ?? '',
      contactPhone: contactPhone ?? '',
      contactEmail: contactEmail ?? '',
      invoiceName: invoiceName ?? '',
      invoiceAddress: invoiceAddress ?? '',
      invoiceEmail: invoiceEmail ?? '',
      invoicePhone: invoicePhone ?? '',
      invoiceVat: invoiceVat ?? '',
      invoiceId: invoiceId ?? '',
      billingOutcome,
      customerId: customerId ?? null,
      customerName,
      status,
    })
  }, [
    customerId,
    customerName,
    contactAddress,
    contactEmail,
    contactName,
    contactPhone,
    deviceDescription,
    devicePassword,
    deviceType,
    fault,
    invoiceAddress,
    invoiceEmail,
    invoiceId,
    invoiceName,
    invoicePhone,
    invoiceVat,
    billingOutcome,
    numberTag,
    peripherals,
    status,
  ])

  const isLocked = isServiceLocked(status)
  // Amend mode reopens the *amendable* fields on a locked job. Everything that
  // writes the job record directly — customer, tag, device password, status —
  // stays closed, because an addendum layers over the record instead of
  // rewriting it and so cannot honour a unique index or re-encrypt a secret.
  const isImmutable = isLocked && !amending
  // While locked, the input still holds the stored ciphertext; after a
  // reveal it holds plaintext, so compare against the revealed value instead
  // of the (encrypted) baseline.
  const devicePasswordLocked = isEncryptedDevicePassword(devicePasswordInput)
  const devicePasswordDirty = devicePasswordLocked
    ? false
    : revealedDevicePassword !== null
      ? devicePasswordInput !== revealedDevicePassword
      : devicePasswordInput !== baseline.devicePassword
  const detailsDirty =
    deviceTypeInput !== baseline.deviceType ||
    deviceDescriptionInput !== baseline.deviceDescription ||
    devicePasswordDirty ||
    numberTagInput !== baseline.numberTag ||
    faultInput !== baseline.fault ||
    peripheralsInput !== baseline.peripherals ||
    invoiceIdInput !== baseline.invoiceId
  // Per-section saves write the job record directly, so they stay shut whenever
  // the job is locked — including while amending, where the batch is committed
  // as an addendum instead.
  const detailsSaveEnabled = !isLocked && detailsDirty
  const contactDirty =
    contactAddressInput !== baseline.contactAddress ||
    contactNameInput !== baseline.contactName ||
    contactPhoneInput !== baseline.contactPhone ||
    contactEmailInput !== baseline.contactEmail
  const invoiceDirty =
    invoiceNameInput !== baseline.invoiceName ||
    invoiceAddressInput !== baseline.invoiceAddress ||
    invoiceEmailInput !== baseline.invoiceEmail ||
    invoicePhoneInput !== baseline.invoicePhone ||
    invoiceVatInput !== baseline.invoiceVat ||
    invoiceIdInput !== baseline.invoiceId
  const customerDirty = (selectedCustomer?.id ?? null) !== baseline.customerId

  const saveDetails = async (payload: {
    contactAddress?: string
    contactName?: string
    contactPhone?: string
    contactEmail?: string
    invoiceName?: string
    invoiceAddress?: string
    invoiceEmail?: string
    invoicePhone?: string
    invoiceVat?: string
    deviceType?: string
    deviceDescription?: string
    devicePassword?: string
    numberTag?: string
    fault: string
    peripherals?: string
    invoiceId?: string
  }) => {
    if (!onSaveDetails) return
    await Promise.resolve(onSaveDetails(payload))
  }

  const handleSaveDetails = async () => {
    if ((!detailsDirty && !contactDirty && !invoiceDirty) || isLocked) return

    const normalizedTag = normalizeJobNumberTag(numberTagInput)
    const conflict = findJobNumberTagConflict(allJobs, serviceId, normalizedTag)
    if (conflict) {
      setTagConflict(conflict)
      return
    }

    try {
      await saveDetails({
        deviceType: deviceTypeInput || undefined,
        deviceDescription: deviceDescriptionInput || undefined,
        devicePassword: devicePasswordInput || undefined,
        numberTag: normalizedTag || undefined,
        fault: faultInput,
        peripherals: peripheralsInput || undefined,
        contactAddress: contactAddressInput || undefined,
        contactName: contactNameInput || undefined,
        contactPhone: contactPhoneInput || undefined,
        contactEmail: contactEmailInput || undefined,
        invoiceName: invoiceNameInput || undefined,
        invoiceAddress: invoiceAddressInput || undefined,
        invoiceEmail: invoiceEmailInput || undefined,
        invoicePhone: invoicePhoneInput || undefined,
        invoiceVat: invoiceVatInput || undefined,
        invoiceId: invoiceIdInput || undefined,
      })
    } catch (error) {
      if (isPocketBaseJobNumberTagUniqueError(error)) {
        const freshConflict = findJobNumberTagConflict(allJobs, serviceId, normalizedTag)
        setTagConflict(
          freshConflict ?? {
            tag: normalizedTag,
            message: `Number tag ${normalizedTag} is already assigned to another job.`,
          },
        )
        return
      }
      throw error
    }

    setNumberTagInput(normalizedTag)
    if (!devicePasswordLocked) setRevealedDevicePassword(devicePasswordInput)
    setBaseline((prev) => ({
      ...prev,
      deviceType: deviceTypeInput,
      deviceDescription: deviceDescriptionInput,
      devicePassword: devicePasswordInput,
      numberTag: normalizedTag,
      fault: faultInput,
      peripherals: peripheralsInput,
      contactAddress: contactAddressInput,
      contactName: contactNameInput,
      contactPhone: contactPhoneInput,
      contactEmail: contactEmailInput,
      invoiceName: invoiceNameInput,
      invoiceAddress: invoiceAddressInput,
      invoiceEmail: invoiceEmailInput,
      invoicePhone: invoicePhoneInput,
      invoiceVat: invoiceVatInput,
      invoiceId: invoiceIdInput,
    }))
  }

  const handleResetDetails = () => {
    setDeviceTypeInput(baseline.deviceType)
    setDeviceDescriptionInput(baseline.deviceDescription)
    setDevicePasswordInput(baseline.devicePassword)
    setRevealedDevicePassword(null)
    setNumberTagInput(baseline.numberTag)
    setFaultInput(baseline.fault)
    setPeripheralsInput(baseline.peripherals)
    setContactAddressInput(baseline.contactAddress)
    setContactNameInput(baseline.contactName)
    setContactPhoneInput(baseline.contactPhone)
    setContactEmailInput(baseline.contactEmail)
    setInvoiceNameInput(baseline.invoiceName)
    setInvoiceAddressInput(baseline.invoiceAddress)
    setInvoiceEmailInput(baseline.invoiceEmail)
    setInvoicePhoneInput(baseline.invoicePhone)
    setInvoiceVatInput(baseline.invoiceVat)
    setInvoiceIdInput(baseline.invoiceId)
  }

  const handleRevealDevicePassword = async () => {
    const passcode = revealPasscode.trim()
    if (!passcode || isRevealing) return
    const pb = (db as ReturnType<typeof useDb> & { pb?: PocketBase }).pb
    if (!pb) {
      setRevealError('Revealing passwords requires the live backend.')
      return
    }
    setIsRevealing(true)
    setRevealError(null)
    try {
      const response = (await pb.send('/api/jobs/device-password/reveal', {
        method: 'POST',
        body: { jobId: serviceId, passcode },
      })) as { password?: string }
      const plain = typeof response?.password === 'string' ? response.password : ''
      setRevealedDevicePassword(plain)
      setDevicePasswordInput(plain)
      setShowDevicePassword(true)
      setRevealDialogOpen(false)
      setRevealPasscode('')
    } catch (error) {
      const status = (error as { status?: number })?.status
      setRevealError(status === 403 ? 'Incorrect passcode.' : 'Unable to reveal the password right now.')
    } finally {
      setIsRevealing(false)
    }
  }

  const handleSaveCustomer = async () => {
    if (!onChangeCustomer || !selectedCustomer?.id || !customerDirty || isLocked) return
    await Promise.resolve(onChangeCustomer(String(selectedCustomer.id)))
    setBaseline((prev) => ({
      ...prev,
      customerId: String(selectedCustomer.id),
      customerName: selectedCustomer.name,
    }))
  }

  const handleResetCustomer = () => {
    if (isLocked) return
    setSelectedCustomer(
      baseline.customerId ? { id: baseline.customerId, name: baseline.customerName } : null,
    )
  }

  const allJobs = useMemo(
    () => Array.from(db.jobsCollection.state.values()) as import('@/lib/schemas/db').Job[],
    [db.jobsCollection.state],
  )

  const canAssignTag = (() => {
    const trimmedInput = normalizeJobNumberTag(numberTagInput)
    const trimmedBaseline = normalizeJobNumberTag(baseline.numberTag)
    return Boolean(trimmedInput) && trimmedInput !== trimmedBaseline
  })()

  const handleAssignTag = async () => {
    if (!canAssignTag || isLocked) return

    const normalizedTag = normalizeJobNumberTag(numberTagInput)
    const conflict = findJobNumberTagConflict(allJobs, serviceId, normalizedTag)
    if (conflict) {
      setTagConflict(conflict)
      return
    }

    try {
      await saveDetails({
        deviceType: deviceTypeInput || undefined,
        deviceDescription: deviceDescriptionInput || undefined,
        devicePassword: devicePasswordInput || undefined,
        numberTag: normalizedTag,
        fault: faultInput,
        peripherals: peripheralsInput || undefined,
        contactAddress: contactAddressInput || undefined,
        contactName: contactNameInput || undefined,
        contactPhone: contactPhoneInput || undefined,
        contactEmail: contactEmailInput || undefined,
        invoiceName: invoiceNameInput || undefined,
        invoiceAddress: invoiceAddressInput || undefined,
        invoiceEmail: invoiceEmailInput || undefined,
        invoicePhone: invoicePhoneInput || undefined,
        invoiceVat: invoiceVatInput || undefined,
        invoiceId: invoiceIdInput || undefined,
      })
    } catch (error) {
      if (isPocketBaseJobNumberTagUniqueError(error)) {
        const freshConflict = findJobNumberTagConflict(allJobs, serviceId, normalizedTag)
        setTagConflict(
          freshConflict ?? {
            tag: normalizedTag,
            message: `Number tag ${normalizedTag} is already assigned to another job.`,
          },
        )
        return
      }
      throw error
    }

    setNumberTagInput(normalizedTag)
    if (!devicePasswordLocked) setRevealedDevicePassword(devicePasswordInput)
    setBaseline((prev) => ({
      ...prev,
      deviceType: deviceTypeInput,
      deviceDescription: deviceDescriptionInput,
      devicePassword: devicePasswordInput,
      numberTag: normalizedTag,
      fault: faultInput,
      peripherals: peripheralsInput,
      contactAddress: contactAddressInput,
      contactName: contactNameInput,
      contactPhone: contactPhoneInput,
      contactEmail: contactEmailInput,
      invoiceName: invoiceNameInput,
      invoiceAddress: invoiceAddressInput,
      invoiceEmail: invoiceEmailInput,
      invoicePhone: invoicePhoneInput,
      invoiceVat: invoiceVatInput,
      invoiceId: invoiceIdInput,
    }))
  }

  const users = useMemo(() => (usersResult.data ?? []) as import('@/db/domain/models').User[], [usersResult.data])

  const amend = useServiceAmend(props, {
    customerId: selectedCustomer?.id != null ? String(selectedCustomer.id) : '',
    customerName: selectedCustomer?.name ?? '',
    fault: faultInput,
    deviceType: deviceTypeInput,
    deviceDescription: deviceDescriptionInput,
    peripherals: peripheralsInput,
    contactAddress: contactAddressInput,
    contactName: contactNameInput,
    contactPhone: contactPhoneInput,
    contactEmail: contactEmailInput,
    invoiceName: invoiceNameInput,
    invoiceEmail: invoiceEmailInput,
    invoicePhone: invoicePhoneInput,
    invoiceAddress: invoiceAddressInput,
    invoiceVat: invoiceVatInput,
    invoiceId: invoiceIdInput,
  }, 'jobs')

  const body = (
    <>
      <CustomerSection
        customerName={customerName}
        customerId={customerId}
        customerPublicId={customerPublicId}
        selectedCustomer={selectedCustomer}
        isImmutable={isImmutable}
        saveEnabled={!isLocked && customerDirty}
        details={linkedCustomer}
        onSelectCustomer={setSelectedCustomer}
        onSaveCustomer={handleSaveCustomer}
        onResetCustomer={handleResetCustomer}
      />

      <section className="rounded-md border border-slate-200 bg-white p-4 space-y-4">
        <div className="flex items-center justify-between">
          <FormCapsLabel>Job details</FormCapsLabel>
          <SectionActions
            saveEnabled={detailsSaveEnabled}
            onSave={() => void handleSaveDetails()}
            onReset={handleResetDetails}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-2 text-xs">
            <div className="space-y-1">
              <FormFieldLabel className="w-32">Type</FormFieldLabel>
              <UnderlineInput disabled={isImmutable} value={deviceTypeInput} onChange={(event) => setDeviceTypeInput(event.target.value)} />
            </div>
            <div className="space-y-1">
              <FormFieldLabel className="w-32">Device(s) description</FormFieldLabel>
              <UnderlineInput
                disabled={isImmutable}
                value={deviceDescriptionInput}
                onChange={(event) => setDeviceDescriptionInput(event.target.value)}
                placeholder="Brand, model, quantity, or identifying notes"
              />
            </div>
            <div className="space-y-1">
              <FormFieldLabel className="w-32">Password</FormFieldLabel>
              <div className="flex items-center gap-2">
                <UnderlineInput
                  disabled={isLocked}
                  readOnly={devicePasswordLocked}
                  disableAutoSelect={devicePasswordLocked}
                  type={devicePasswordLocked || !showDevicePassword ? 'password' : 'text'}
                  autoComplete="off"
                  value={devicePasswordLocked ? '••••••••' : devicePasswordInput}
                  onChange={(event) => {
                    if (!devicePasswordLocked) setDevicePasswordInput(event.target.value)
                  }}
                />
                {devicePasswordLocked ? (
                  <button
                    type="button"
                    className="shrink-0 text-slate-400 hover:text-slate-700"
                    aria-label="Reveal password (passcode required)"
                    title="Reveal password (passcode required)"
                    onClick={() => {
                      setRevealPasscode('')
                      setRevealError(null)
                      setRevealDialogOpen(true)
                    }}
                  >
                    <Lock className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="shrink-0 text-slate-400 hover:text-slate-700"
                    aria-label={showDevicePassword ? 'Hide password' : 'Show password'}
                    title={showDevicePassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowDevicePassword((value) => !value)}
                  >
                    {showDevicePassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <FormFieldLabel className="w-32">Tag</FormFieldLabel>
              <UnderlineInput disabled={isLocked} value={numberTagInput} onChange={(event) => setNumberTagInput(event.target.value)} />
            </div>
            {/* The Omni invoice number lives in Invoice details, with the
                other invoicing fields. It used to be duplicated here over the
                same state, so editing it lit up two Save buttons. */}
            <div className="space-y-1">
              <FormFieldLabel className="w-32">Billing outcome</FormFieldLabel>
              <UnderlineInput
                readOnly
                disableAutoSelect
                value={formatBillingOutcome(billingOutcome, invoiceId)}
                className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none"
              />
            </div>
          </div>

          <div className="space-y-2 text-xs">
            <div className="space-y-1">
              <FormFieldLabel className="w-32">Fault</FormFieldLabel>
              <UnderlineTextarea disabled={isImmutable} value={faultInput} onChange={(event) => setFaultInput(event.target.value)} rows={4} />
            </div>
            <div className="space-y-1">
              <FormFieldLabel className="w-32">Peripherals</FormFieldLabel>
              <UnderlineTextarea disabled={isImmutable} value={peripheralsInput} onChange={(event) => setPeripheralsInput(event.target.value)} rows={4} />
            </div>
          </div>
        </div>
      </section>

      <ContactSection
        contactAddress={contactAddressInput}
        contactName={contactNameInput}
        contactPhone={contactPhoneInput}
        contactEmail={contactEmailInput}
        fieldOrder={['contactName', 'contactPhone', 'contactEmail', 'contactAddress']}
        title="Check in Contact Details"
        addressLabel="Address"
        nameLabel="Name"
        isImmutable={isImmutable}
        saveEnabled={!isLocked && contactDirty}
        onChange={(fields) => {
          if (fields.contactAddress !== undefined) setContactAddressInput(fields.contactAddress)
          if (fields.contactName !== undefined) setContactNameInput(fields.contactName)
          if (fields.contactPhone !== undefined) setContactPhoneInput(fields.contactPhone)
          if (fields.contactEmail !== undefined) setContactEmailInput(fields.contactEmail)
        }}
        onSave={() => void handleSaveDetails()}
        onReset={() => {
          setContactAddressInput(baseline.contactAddress)
          setContactNameInput(baseline.contactName)
          setContactPhoneInput(baseline.contactPhone)
          setContactEmailInput(baseline.contactEmail)
        }}
      />

      <InvoiceSection
        customerInvoice={pickComparisonCustomer({
          selected: selectedCustomer as never,
          currentCustomerId: customerId,
          linked: linkedCustomer,
        })}
        invoiceName={invoiceNameInput}
        invoiceEmail={invoiceEmailInput}
        invoicePhone={invoicePhoneInput}
        invoiceAddress={invoiceAddressInput}
        invoiceVat={invoiceVatInput}
        invoiceId={invoiceIdInput}
        isImmutable={isImmutable}
        saveEnabled={!isLocked && invoiceDirty}
        onChange={(fields) => {
          if (fields.invoiceName !== undefined) setInvoiceNameInput(fields.invoiceName)
          if (fields.invoiceEmail !== undefined) setInvoiceEmailInput(fields.invoiceEmail)
          if (fields.invoicePhone !== undefined) setInvoicePhoneInput(fields.invoicePhone)
          if (fields.invoiceAddress !== undefined) setInvoiceAddressInput(fields.invoiceAddress)
          if (fields.invoiceVat !== undefined) setInvoiceVatInput(fields.invoiceVat)
          if (fields.invoiceId !== undefined) setInvoiceIdInput(fields.invoiceId)
        }}
        onSave={() => void handleSaveDetails()}
        onReset={() => {
          setInvoiceNameInput(baseline.invoiceName)
          setInvoiceAddressInput(baseline.invoiceAddress)
          setInvoiceEmailInput(baseline.invoiceEmail)
          setInvoicePhoneInput(baseline.invoicePhone)
          setInvoiceVatInput(baseline.invoiceVat)
          setInvoiceIdInput(baseline.invoiceId)
        }}
      />

      <section className="rounded-md border border-slate-200 bg-white p-4 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <FormCapsLabel>Assignment</FormCapsLabel>
            <p className="mt-1 text-xs text-slate-500">
              Assigned to {assignedUserName ?? 'nobody yet'}.
            </p>
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            disabled={isLocked}
            onClick={() => setAssignmentDialogOpen(true)}
          >
            Assign job
          </button>
        </div>

        <div className="space-y-2">
          <FormCapsLabel>Assignment history</FormCapsLabel>
          {assignmentHistory.length === 0 ? (
            <p className="text-sm text-slate-500">No assignment history yet.</p>
          ) : (
            <div className="space-y-2">
              {assignmentHistory.map((entry) => (
                <div key={entry.id} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-sm font-medium text-slate-900">{entry.assignedUserName}</p>
                  <p className="text-xs text-slate-500">{formatAssignmentWindow(entry.assignedAt, entry.unassignedAt)}</p>
                  <p className="text-xs text-slate-500">Assigned by {entry.authorName}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="rounded-md border border-slate-200 bg-white p-4 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <FormCapsLabel>Tag</FormCapsLabel>
            <p className="mt-1 text-xs text-slate-500">
              Assign the job number tag.
            </p>
          </div>
          <button
            type="button"
            className={
              canAssignTag
                && !isLocked
                ? 'inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700'
                : 'inline-flex items-center gap-2 rounded-md bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-400'
            }
            disabled={!canAssignTag || isLocked}
            onClick={() => void handleAssignTag()}
          >
            Assign tag
          </button>
        </div>
        <div className="space-y-1 text-xs">
          <FormFieldLabel className="w-32">Tag</FormFieldLabel>
          <UnderlineInput disabled={isLocked} value={numberTagInput} onChange={(event) => setNumberTagInput(event.target.value)} />
        </div>
      </section>

      {/* Status, its reason and the actions live on the Log Work tab's
          StatusCard; this tab no longer duplicates them. */}
      {status === 'ReadyForCollectionUnsuccessful' ? (
        <section className="rounded-md border border-red-300 bg-red-50 p-4">
          <FormCapsLabel className="text-red-700">Unsuccessful reason</FormCapsLabel>
          <p className="mt-2 text-sm text-red-900">
            {getCurrentUnsuccessfulReason(status, statusChanges) ||
              'No reason captured.'}
          </p>
        </section>
      ) : null}

      <section className="rounded-md border border-slate-200 bg-white p-4">
        <FormCapsLabel>Check in / out</FormCapsLabel>
        <dl className="mt-3 space-y-3 text-sm text-slate-700">
          {assignedUserName ? <div className="flex gap-3"><dt className="w-32 text-slate-500">Assigned to</dt><dd>{assignedUserName}</dd></div> : null}
          {checkedInSignedAt ? <div className="flex gap-3"><dt className="w-32 text-slate-500">Checked in</dt><dd>{formatLocalDateTime(checkedInSignedAt)}</dd></div> : null}
          {checkedInSignedAt ? (
            <div className="flex gap-3">
              <dt className="w-32 text-slate-500">Check-in sig</dt>
              <dd>{checkedInSignatureUrl ? 'Captured' : checkedInNoSignatureReason ? 'Skipped' : 'Not captured'}</dd>
            </div>
          ) : null}
          {checkedInSignatureUrl ? (
            <div className="flex items-start gap-3">
              <dt className="w-32 pt-2 text-slate-500">Signature</dt>
              <dd>
                <a
                  href={checkedInSignatureUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block overflow-hidden rounded-md border border-slate-200 bg-slate-50 hover:border-slate-300"
                >
                  <img
                    src={checkedInSignatureUrl}
                    alt="Check-in signature"
                    className="h-16 w-32 object-contain bg-white"
                  />
                </a>
              </dd>
            </div>
          ) : null}
          {checkedInNoSignatureReason ? (
            <div className="flex gap-3">
              <dt className="w-32 text-slate-500">Check-in reason</dt>
              <dd>
                {checkedInNoSignatureReason}
                {checkedInByName ? ` (${checkedInByName})` : ''}
              </dd>
            </div>
          ) : null}
          {checkedInByName ? <div className="flex gap-3"><dt className="w-32 text-slate-500">Processed by</dt><dd>{checkedInByName}</dd></div> : null}
          {checkedOutSignedAt ? <div className="flex gap-3"><dt className="w-32 text-slate-500">Checked out</dt><dd>{formatLocalDateTime(checkedOutSignedAt)}</dd></div> : null}
          {checkedOutSignedAt ? (
            <div className="flex gap-3">
              <dt className="w-32 text-slate-500">Check-out sig</dt>
              <dd>{checkedOutSignatureUrl ? 'Captured' : checkedOutNoSignatureReason ? 'Skipped' : 'Not captured'}</dd>
            </div>
          ) : null}
          {checkedOutSignatureUrl ? (
            <div className="flex items-start gap-3">
              <dt className="w-32 pt-2 text-slate-500">Check-out sig</dt>
              <dd>
                <a
                  href={checkedOutSignatureUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block overflow-hidden rounded-md border border-slate-200 bg-slate-50 hover:border-slate-300"
                >
                  <img
                    src={checkedOutSignatureUrl}
                    alt="Check-out signature"
                    className="h-16 w-32 object-contain bg-white"
                  />
                </a>
              </dd>
            </div>
          ) : null}
          {checkedOutName ? <div className="flex gap-3"><dt className="w-32 text-slate-500">Checked out by</dt><dd>{checkedOutName}</dd></div> : null}
          {checkedOutByName ? <div className="flex gap-3"><dt className="w-32 text-slate-500">Processed by</dt><dd>{checkedOutByName}</dd></div> : null}
          {checkedOutPhone ? <div className="flex gap-3"><dt className="w-32 text-slate-500">Phone</dt><dd>{checkedOutPhone}</dd></div> : null}
          {checkedOutNoSignatureReason ? (
            <div className="flex gap-3">
              <dt className="w-32 text-slate-500">Check-out reason</dt>
              <dd>
                {checkedOutNoSignatureReason}
                {checkedOutByName ? ` (${checkedOutByName})` : ''}
              </dd>
            </div>
          ) : null}
          {!checkedInSignedAt && !checkedInSignatureUrl && !checkedInNoSignatureReason && !checkedOutSignedAt && !checkedOutSignatureUrl && !checkedOutName && !checkedOutPhone && !checkedOutNoSignatureReason ? (
            <div className="text-sm text-slate-500">No check-in or check-out details captured yet.</div>
          ) : null}
        </dl>
      </section>
    </>
  )

  const withDialog = (
    <>
      {body}
      <Dialog open={tagConflict !== null} onOpenChange={(open) => {
        if (!open) setTagConflict(null)
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Number tag already assigned</DialogTitle>
            <DialogDescription>
              {tagConflict?.message}
            </DialogDescription>
          </DialogHeader>
          {tagConflict?.conflictingJob ? (
            <div className="text-sm text-slate-600">
              Assigned job:{' '}
              <Link to="/jobs/$id" params={{ id: tagConflict.conflictingJob.id }} className="font-medium text-blue-700 hover:underline">
                {tagConflict.conflictingJob.publicId ?? tagConflict.conflictingJob.id}
              </Link>
            </div>
          ) : null}
          <DialogFooter>
            <button
              type="button"
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
              onClick={() => setTagConflict(null)}
            >
              Close
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={revealDialogOpen}
        onOpenChange={(open) => {
          setRevealDialogOpen(open)
          if (!open) {
            setRevealPasscode('')
            setRevealError(null)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reveal device password</DialogTitle>
            <DialogDescription>
              Client passwords are stored encrypted. Enter the master passcode to view this one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <FormCapsLabel>Master passcode</FormCapsLabel>
            <UnderlineInput
              type="password"
              autoComplete="off"
              autoFocus
              value={revealPasscode}
              onChange={(event) => setRevealPasscode(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void handleRevealDevicePassword()
              }}
            />
            {revealError ? <p className="text-xs text-red-600">{revealError}</p> : null}
          </div>
          <DialogFooter>
            <button
              type="button"
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => setRevealDialogOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!revealPasscode.trim() || isRevealing}
              onClick={() => void handleRevealDevicePassword()}
            >
              {isRevealing ? 'Revealing…' : 'Reveal'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <JobAssignmentDialog
        open={assignmentDialogOpen}
        onOpenChange={setAssignmentDialogOpen}
        jobLabel={publicId ?? serviceId}
        users={users}
        currentUserId={db.getCurrentUser()?.id}
        assignedUserId={assignedUserId}
        onAssign={async (userId) => {
          await Promise.resolve(onAssignJob?.(userId))
        }}
      />
    </>
  )

  return (
    <ServiceDetailsShell
      resourceLabel="Job"
      resourcePlural="jobs"
      title="Job details"
      blurb="View and edit job device, fault, linked customer, and invoice details."
      idLabel="Job ID"
      idValue={publicId ?? serviceId}
      status={status}
      statusChanges={statusChanges}
      getAuthorDisplayName={getAuthorDisplayName}
      showHeader={showHeader}
      amend={amend}
      revisions={props.revisions}
      unlock={{
        actionLabel: 'Move to Ready to Work',
        targetStatus: 'ReadyToWork',
        requireReason: status === 'Invoiced',
        actionTitle: 'Move this job back to Ready to Work?',
        actionDescription:
          'Add a short reason so the office team knows why this job is being moved back to Ready to Work.',
        lockedMessage: `This job is locked. No changes can be made while it is ${status}. Move it back to Ready to Work to unlock.`,
        reasonLabel: status === 'Invoiced' ? 'Reason (required)' : 'Reason',
        reasonPlaceholder:
          'Required. E.g. customer returned, payment issue, more work needed…',
        confirmLabel: 'Move to Ready to Work',
        cancelLabel: status === 'Invoiced' ? 'Keep Invoiced' : 'Keep Cancelled',
        onChangeStatus: (nextStatus, reason) =>
          void onChangeStatus?.(nextStatus, reason),
      }}
    >
      {withDialog}
    </ServiceDetailsShell>
  )
}
